import { useEffect, useState } from "react";
import { Modal, Select, message, Form, Tabs, Input } from "antd";
import { getClients } from "../../api/client";

const { Option } = Select;
const { TabPane } = Tabs;

export default function SendDocumentsModal({
    open,
    onCancel,
    onSubmit,
    loading
}) {
    const [form] = Form.useForm();
    const [clients, setClients] = useState([]);
    const [loadingClients, setLoadingClients] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [activeTab, setActiveTab] = useState("sms");
    const [emailInputMethod, setEmailInputMethod] = useState("client");

    useEffect(() => {
        if (open) {
            fetchClients();
            form.resetFields();
            setSelectedClient(null);
            setContacts([]);
            setActiveTab("sms");
            setEmailInputMethod("client");
        }
    }, [open]);

    const fetchClients = async () => {
        setLoadingClients(true);
        try {
            const { results } = await getClients({ page: 1, perPage: 100 });
            setClients(results || []);
        } catch (err) {
            console.error(err);
            message.error("Failed to load clients");
        } finally {
            setLoadingClients(false);
        }
    };

    const handleClientChange = (clientId) => {
        const client = clients.find(c => c.id === clientId);
        setSelectedClient(client);

        // Filter contacts based on active tab
        let clientContacts = [];
        if (activeTab === "sms") {
            clientContacts = client?.contacts?.filter(
                contact => contact.isActive && contact.phone
            ) || [];
        } else {
            // For email tab
            clientContacts = client?.contacts?.filter(
                contact => contact.isActive && contact.email
            ) || [];
        }

        setContacts(clientContacts);

        // Reset contact field when client changes
        form.setFieldsValue({ contactId: undefined, manualEmail: undefined });

        if (clientContacts.length === 0) {
            const contactType = activeTab === "sms" ? "phone numbers" : "email addresses";
            message.warning(`This client has no contacts with ${contactType}`);
        }
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        form.resetFields();
        setSelectedClient(null);
        setContacts([]);
        setEmailInputMethod("client");
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();

            const submitData = {
                deliveryMethod: activeTab,
            };

            if (activeTab === "sms") {
                const contact = contacts.find(c => c.id === values.contactId);
                if (!contact || !contact.phone) {
                    message.error("Selected contact does not have a phone number");
                    return;
                }
                submitData.clientId = values.clientId;
                submitData.contactId = values.contactId;
            } else {
                // Email tab
                if (emailInputMethod === "manual") {
                    submitData.manualEmail = values.manualEmail;
                } else {
                    const contact = contacts.find(c => c.id === values.contactId);
                    if (!contact || !contact.email) {
                        message.error("Selected contact does not have an email address");
                        return;
                    }
                    submitData.clientId = values.clientId;
                    submitData.contactId = values.contactId;
                }
            }

            await onSubmit(submitData);

            form.resetFields();
            setSelectedClient(null);
            setContacts([]);
            setEmailInputMethod("client");
        } catch (err) {
            console.error("Validation failed:", err);
        }
    };

    const canSubmit = () => {
        if (activeTab === "sms") {
            return contacts.length > 0;
        } else {
            // Email tab
            if (emailInputMethod === "manual") {
                return true;
            } else {
                return contacts.length > 0;
            }
        }
    };

    return (
        <Modal
            title="Send Equipment Documents"
            open={open}
            onOk={handleOk}
            onCancel={onCancel}
            okText="Send Documents"
            confirmLoading={loading}
            okButtonProps={{ disabled: !canSubmit() }}
            width={600}
        >
            <Form
                form={form}
                layout="vertical"
            >
                <Tabs activeKey={activeTab} onChange={handleTabChange} className="mt-4">
                    <TabPane tab="Mobile (SMS)" key="sms">
                        <div className="mt-4">
                            <Form.Item
                                label="Select Client"
                                name="clientId"
                                rules={[{ required: activeTab === "sms", message: "Please select a client" }]}
                            >
                                <Select
                                    placeholder="Choose a client"
                                    onChange={handleClientChange}
                                    loading={loadingClients}
                                    showSearch
                                    filterOption={(input, option) =>
                                        String(option?.children ?? '')
                                            .toLowerCase()
                                            .includes(String(input || '').toLowerCase())
                                    }
                                >
                                    {clients.map(client => (
                                        <Option key={client.id} value={client.id}>
                                            {client.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="Select Contact"
                                name="contactId"
                                rules={[{ required: activeTab === "sms", message: "Please select a contact" }]}
                            >
                                <Select
                                    placeholder="Choose a contact"
                                    disabled={!selectedClient || contacts.length === 0}
                                    showSearch
                                    filterOption={(input, option) =>
                                        String(option?.children ?? '')
                                            .toLowerCase()
                                            .includes(String(input || '').toLowerCase())
                                    }
                                >
                                    {contacts.map(contact => (
                                        <Option key={contact.id} value={contact.id}>
                                            {`${contact.firstName} ${contact.lastName}`}
                                            {contact.phone && ` - ${contact.phone}`}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            {selectedClient && contacts.length === 0 && (
                                <div className="text-orange-600 text-sm mb-2">
                                    This client has no contacts with phone numbers. Please add a contact with a phone number first.
                                </div>
                            )}
                        </div>
                    </TabPane>

                    <TabPane tab="Email" key="email">
                        <div className="mt-4">
                            <Form.Item label="Email Input Method">
                                <Select
                                    value={emailInputMethod}
                                    onChange={(value) => {
                                        setEmailInputMethod(value);
                                        if (value === "manual") {
                                            form.setFieldsValue({ clientId: undefined, contactId: undefined });
                                        } else {
                                            form.setFieldsValue({ manualEmail: undefined });
                                        }
                                        setSelectedClient(null);
                                        setContacts([]);
                                    }}
                                >
                                    <Option value="client">Select from Client Contacts</Option>
                                    <Option value="manual">Enter Email Manually</Option>
                                </Select>
                            </Form.Item>

                            {emailInputMethod === "client" ? (
                                <>
                                    <Form.Item
                                        label="Select Client"
                                        name="clientId"
                                        rules={[{ required: activeTab === "email" && emailInputMethod === "client", message: "Please select a client" }]}
                                    >
                                        <Select
                                            placeholder="Choose a client"
                                            onChange={handleClientChange}
                                            loading={loadingClients}
                                            showSearch
                                            filterOption={(input, option) =>
                                                String(option?.children ?? '')
                                                    .toLowerCase()
                                                    .includes(String(input || '').toLowerCase())
                                            }
                                        >
                                            {clients.map(client => (
                                                <Option key={client.id} value={client.id}>
                                                    {client.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    <Form.Item
                                        label="Select Contact"
                                        name="contactId"
                                        rules={[{ required: activeTab === "email" && emailInputMethod === "client", message: "Please select a contact" }]}
                                    >
                                        <Select
                                            placeholder="Choose a contact"
                                            disabled={!selectedClient || contacts.length === 0}
                                            showSearch
                                            filterOption={(input, option) =>
                                                String(option?.children ?? '')
                                                    .toLowerCase()
                                                    .includes(String(input || '').toLowerCase())
                                            }
                                        >
                                            {contacts.map(contact => (
                                                <Option key={contact.id} value={contact.id}>
                                                    {`${contact.firstName} ${contact.lastName}`}
                                                    {contact.email && ` - ${contact.email}`}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    {selectedClient && contacts.length === 0 && (
                                        <div className="text-orange-600 text-sm mb-2">
                                            This client has no contacts with email addresses. Please add a contact with an email or use manual entry.
                                        </div>
                                    )}
                                </>
                            ) : (
                                <Form.Item
                                    label="Email Address"
                                    name="manualEmail"
                                    rules={[
                                        { required: activeTab === "email" && emailInputMethod === "manual", message: "Please enter an email address" },
                                        { type: "email", message: "Please enter a valid email address" }
                                    ]}
                                >
                                    <Input
                                        placeholder="Enter email address"
                                        type="email"
                                    />
                                </Form.Item>
                            )}
                        </div>
                    </TabPane>
                </Tabs>
            </Form>
        </Modal>
    );
}
